import {
  Button,
  Classes,
  Colors,
  Dialog,
  FormGroup,
  TextArea,
} from "@blueprintjs/core";
import styled from "@emotion/styled";

import { isCoach } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { Checkbox } from "@/components/pieces/form/Checkbox";
import { Avatar } from "@/components/pieces/users/Avatar";
import { SUBTEXT_COLOR } from "@/css/constants";
import { MeetingCoach } from "@/types/coaches";
import { DetailQueryMeeting } from "@/types/meetings";
import { formatDate } from "@/utils/datetime";
import { formatCredits } from "@/utils/string";

import { isMeetingRefundable } from "./is-meeting-refundable";

type MeetingProduct = NonNullable<DetailQueryMeeting["product"]>;

interface PayScheduleTextProps {
  isRefundException: boolean;
  onToggleReschedule: (on: boolean) => void;
  creditPriceWhenOrder?: string | null;
}

function PayScheduleText({
  isRefundException,
  onToggleReschedule,
  creditPriceWhenOrder = "0.00",
}: PayScheduleTextProps) {
  return (
    <Box>
      This meeting <b>does not qualify</b> for an automatic, free reschedule
      because it is happening too soon.
      {isCoach() ? (
        <>
          {" "}
          As the coach, you have the option to refund this meeting anyways.
          <br />
          <br />
          You will only be compensated for the new, rescheduled meeting if you
          choose to allow a free reschedule.
        </>
      ) : (
        <>
          <br />
          <br />
          Rescheduling this meeting will result in an additional{" "}
          <b>{formatCredits(creditPriceWhenOrder)} charge</b>. Please ensure you
          have enough credits on your account.
        </>
      )}
      <br />
      {isCoach() && (
        <Box mt={3}>
          <Checkbox
            checked={isRefundException}
            onChange={onToggleReschedule}
            label={`Reschedule without charging the customer`}
            style={{ color: SUBTEXT_COLOR, fontSize: 16 }}
          />
        </Box>
      )}
    </Box>
  );
}

interface ScheduleFeeProps {
  product?: MeetingProduct | null;
  meeting?: DetailQueryMeeting | null;
  isRefundException: boolean;
  onToggleReschedule: (on: boolean) => void;
}

function ScheduleFee({
  product,
  meeting,
  isRefundException,
  onToggleReschedule,
}: ScheduleFeeProps) {
  return (
    <FormGroup style={{ marginTop: 20 }} label={<b>Reschedule fee</b>}>
      {product && !isMeetingRefundable(meeting) ? (
        <PayScheduleText
          creditPriceWhenOrder={meeting?.creditPrice}
          isRefundException={isRefundException}
          onToggleReschedule={onToggleReschedule}
        />
      ) : (
        <Box>Meeting qualifies for free reschedule.</Box>
      )}
    </FormGroup>
  );
}

interface NewTimeProps {
  coach?: MeetingCoach | null;
  product?: MeetingProduct | null;
  timeslot?: string | null;
}

function NewTime({ coach, product, timeslot }: NewTimeProps) {
  return (
    <FormGroup style={{ marginTop: 20 }} label={<b>New time</b>}>
      <Box
        className="bas round-corners"
        bg={Colors.WHITE}
        p={16}
        color={SUBTEXT_COLOR}
      >
        {product && product.name && (
          <Box pb={2} color={Colors.BLACK}>
            <b>{product.name}</b>
          </Box>
        )}
        {timeslot && (
          <Box pb={2}>
            {formatDate(timeslot, "hh:mm a z 'on' MMMM do, yyyy")}
          </Box>
        )}

        {coach && (
          <Flex alignItems="center">
            <Avatar user={coach} size={28} />
            <Box ml={1}>{coach.name}</Box>
          </Flex>
        )}
      </Box>
    </FormGroup>
  );
}

interface MessagesForOtherPartyProps {
  message?: string;
  onMessageChange: (message: string) => void;
}

function MessagesForOtherParty({
  message = "",
  onMessageChange,
}: MessagesForOtherPartyProps) {
  return (
    <FormGroup
      style={{ marginTop: 20 }}
      label={<b>Message for {isCoach() ? "client" : "coach"}</b>}
      helperText={`${message.length}/255 Characters`}
    >
      <TextArea
        onChange={(event) => onMessageChange(event.target.value)}
        fill={true}
        placeholder="Add an optional messages"
        autoResize={true}
        large={true}
        value={message}
        maxLength={255}
      />
    </FormGroup>
  );
}

interface ConfirmRescheduleDialogProps {
  visible: boolean;
  onClose: (confirmed: boolean) => void;
  coach?: MeetingCoach | null;
  product?: MeetingProduct | null;
  meeting?: DetailQueryMeeting | null;
  timeslot?: string | null;
  isRefundException: boolean;
  onToggleReschedule: (on: boolean) => void;
  onConfirm: () => void;
  rescheduleReason: string;
  onReasonChange: (reason: string) => void;
}

export function ConfirmRescheduleDialog({
  visible,
  onClose,
  coach,
  product,
  meeting,
  timeslot,
  isRefundException,
  onToggleReschedule,
  onConfirm,
  rescheduleReason,
  onReasonChange,
}: ConfirmRescheduleDialogProps) {
  return (
    <StyledDialog
      isOpen={visible}
      onClose={() => onClose(false)}
      lazy={true}
      title={<Box fontSize={18}>Confirm Reschedule</Box>}
      style={{ width: "480px" }}
    >
      <StyledContainerList
        pb={16}
        px={20}
        backgroundColor="#F6F6FB"
        fontSize="16"
      >
        <ScheduleFee
          product={product}
          meeting={meeting}
          isRefundException={isRefundException}
          onToggleReschedule={onToggleReschedule}
        />
        <NewTime coach={coach} product={product} timeslot={timeslot} />
        <MessagesForOtherParty
          message={rescheduleReason}
          onMessageChange={onReasonChange}
        />
        <FormGroup style={{ marginTop: 20 }}>
          <Flex>
            <Button intent="primary" disabled={false} onClick={onConfirm}>
              {"Reschedule Session"}
            </Button>
            <Button disabled={false} onClick={() => onClose(false)}>
              {"Nevermind"}
            </Button>
          </Flex>
        </FormGroup>
      </StyledContainerList>
    </StyledDialog>
  );
}

const StyledDialog = styled(Dialog)`
  &.${Classes.DIALOG} {
    max-width: 90%;
    padding-bottom: 0;
    .${Classes.DIALOG_HEADER} {
      box-shadow: none;
      padding: 10px 16px;
      background-color: ${Colors.WHITE};
    }
  }
`;

const StyledContainerList = styled(Box)`
  .${Classes.BUTTON} {
    padding: 12px 19px;
    margin-right: 10px;
  }
  .${Classes.BUTTON_TEXT} {
    font-size: 16px;
  }

  .${Classes.FORM_GROUP} {
    margin: 0;
  }

  border-radius: 12px;
`;
